import { RadioCst } from "@components/InputCst";
import Table from "@components/Table";
import { useAuth } from "@context/auth";
import { useFilters } from "@context/filters";
import { useFetchTable } from "@hooks/useFetchDataTable";
import { Box, Divider, Typography, useTheme } from "@mui/material";
import {
  LearningObjectProps,
  Step3Props,
} from "@pages/Contents/Corporate/CollectCampaigns/types";
import { ServicesURL } from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useCollectCampaignState } from "../../context/CollectCampaignContext";

const Step3: React.FC<Step3Props> = ({ errors }) => {
  const { id, idCampaign } = useParams();
  const theme = useTheme();
  const { user } = useAuth();
  const { filters } = useFilters();
  const filterId = `${user?.id_user}_${PagesID["corporate.collect-campaign.add.los"]}`;

  const { t } = useTranslation(["collect-campaign-page"]);
  const { state, dispatch } = useCollectCampaignState();
  const initialSelectedRequests = state?.learning_objects || [];
  const [selectedRequests, setSelectedRequests] = React.useState<
    LearningObjectProps[]
  >(initialSelectedRequests);
  const [allLosSelected, setAllLosSelected] = React.useState<boolean>(
    !(state.learning_objects ?? false),
  );

  const { columns, actionsTopToolbar, dataTable } = useFetchTable(
    `${ServicesURL.learningObjectsStandAlone.replace(
      ":corporateId",
      id ? id : "1",
    )}`,
    PagesID["corporate.collect-campaign.add.los"],
    filters[filterId],
  );

  const options = [
    {
      label: `${t("all_lo")}`,
      value: false,
    },
    {
      label: `${t("selected_los")}`,
      value: true,
    },
  ];

  const handleSelectRequest = (row: { [key: string | number]: boolean }) => {
    setSelectedRequests((prevSelected) => {
      //PREV SELECTIONS
      const prevSelectedIds = new Set(
        prevSelected.map((req) => req.id || req.value),
      );
      //NEW SELECTIONS
      const newSelectedIds = new Set(
        Object.keys(row)
          .filter((key) => row[key])
          .map(Number),
      );

      //TO ADD
      const elementsToAdd = Array.from(newSelectedIds).filter(
        (id) => !prevSelectedIds.has(id),
      );
      //TO REMOVE
      const elementsToRemove = Array.from(prevSelectedIds).filter(
        (id) => !newSelectedIds.has(id as number),
      );

      //NEW LIST UPDATED SELECTIONS
      const newSelectedRequests = prevSelected
        .filter((req) => !elementsToRemove.includes(req.id || req.value))
        .concat(
          dataTable.output.filter((item: any) =>
            elementsToAdd.includes(item.id),
          ),
        );

      const itemsToDispatch = newSelectedRequests.map((item) => ({
        label: item?.title || item?.label,
        value: item?.id || item?.value,
      }));

      //UPDATE STATE
      dispatch({
        type: "SET_LOS",
        payload: newSelectedRequests,
      });

      return newSelectedRequests;
    });
  };

  const selectedRows = selectedRequests.reduce(
    (acc: any, item: LearningObjectProps) => {
      const id = item.id || item.value;
      if (id) {
        acc[id] = true;
      }
      return acc;
    },
    {},
  );

  useMemo(() => {
    if (!dataTable?.output) return;

    return dataTable.output.map((item: any) => {
      switch (item.type) {
        case "COURSE":
        case "Corso":
          item.type = "Corso";
          break;
        case "PATH":
        case "Percorso":
          item.type = "Percorso";
          break;
        default:
          item.type = "Attivitá";
          break;
      }

      return item;
    });
  }, [dataTable]);
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        padding: "16px",
        borderRadius: "3px",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px",
          paddingX: 0,
          // marginBottom: "16px",
        }}
      >
        <RadioCst
          key={"lo"}
          id={"lo"}
          sx={{
            width: "100%",
            "& .MuiFormControlLabel-root": {
              margin: "0 10px 0px 10px",
              border: `1px solid ${theme.palette.divider}`,
              flex: 1,
              whiteSpace: "nowrap",
              paddingX: "20px",
              borderRadius: "3px",
              textAlign: "start",
            },
          }}
          value={!allLosSelected}
          onChange={(e: any) => {
            dispatch({
              type: "SET_LOS",
              payload: [],
            });
            setSelectedRequests([]);
            setAllLosSelected(!(String(e).toLowerCase() === "true"));
          }}
          options={options}
          error={!!errors.learningObjects}
          errorMessage={`${t(`validation.${errors.learningObjects}`)}`}
        />
      </Box>
      <Divider
        variant="middle"
        sx={{
          marginLeft: "0.5rem",
          marginRight: "0.5rem",
        }}
      ></Divider>
      <Box
        sx={
          {
            // mt: "25px",
            // mt: "-24px"
          }
        }
      >
        {!allLosSelected && dataTable && (
          <>
            <Table
              id={PagesID["corporate.collect-campaign.add.los"]}
              data={dataTable?.output || []}
              columns={columns || []}
              configurationTopToolbar={actionsTopToolbar}
              totalPages={dataTable?.metadata?.total_elements}
              enableRowSelection={true}
              enableSelectAll={true}
              getRowId={(row: any) => (row ? row.id : null)}
              onRowSelectionChange={(row: any) => handleSelectRequest(row)}
              selectedRows={selectedRows || initialSelectedRequests}
              enableRowActions={false}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default Step3;
